import { useEffect } from 'react';

import { getWaterfallById } from '@/apiCalls/getWaterfallById';
import { getQuickQuote } from '@/apiCalls/matchingEngine';
// import { Footer } from '@/components/Footer';
import { Loading } from '@/components/Loading';
import { ReviewNav } from '@/components/ReviewNav';
import { EventSubscriber } from '@/components/SseSubscriber';
import {
    deleteSupportingDoc,
    EUserFlow,
    ISupportingDoc,
    selectVehicleMake,
    setSupportingDocs,
} from '@/context/borrower';
import {
    EModals,
    selectInitialLoad,
    selectIsLoaded,
    selectModal,
    selectWidgetLocked,
    selectWidgetLockScreen,
    setModal,
    setWaterfallLenders,
} from '@/context/display';
import { store } from '@/context/store';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { useCopy } from '@/copy';
import { EWidgetLockScreen } from '@/partners/EWidgetLockScreen';
import { setParametersFromParams } from '@/utils/setParametersFromParams';
import { EEvents } from '@/utils/subscribeToSseEvent';

import { DisplayScreen } from '../DisplayScreen';
import { ModalScreen } from '../ModalScreen';
import { WidgetLocked } from '../WidgetLocked';
import { WidgetLockedFirstScreen } from '../WidgetLockedFirstScreen';

function Vroom(): JSX.Element {
    // ***** Redux *****
    const isLoaded = useAppSelector(selectIsLoaded) ?? false;
    const initialLoad = useAppSelector(selectInitialLoad) ?? false;
    const widgetLocked = useAppSelector(selectWidgetLocked);
    const widgetLockScreen = useAppSelector(selectWidgetLockScreen);

    // ***** Helper Functions *****
    if (!initialLoad) {
        const fetchData = async (): Promise<void> => {
            await setParametersFromParams();
            const state = store.getState();
            const isLoggedIn = state.userReducer.isLoggedIn;
            const instantLeadCapture =
                state.displayReducer.partner.userFlow ===
                EUserFlow.INSTANT_LEAD_CAPTURE;

            if (!isLoggedIn && instantLeadCapture) {
                store.dispatch(setModal(EModals.REGISTER));
            }
            // Any other code you need after the parameters are set
        };

        fetchData();
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    useEffect(() => {}, [isLoaded]);

    // ***** Render *****
    if (!isLoaded) return <Loading />;
    // Held closed until this partner's setup fee is paid. Above RealApp, so
    // no screen, modal, SSE subscription or quote request behind it ever
    // mounts — the journey does not start rather than starting and hiding.
    //
    // Safe after isLoaded: the partner payload is in the store by then, so
    // this cannot flash the journey before the flag is known.
    //
    // This is a commercial gate, not a security one. It decides what the
    // browser renders, so it holds against a customer and not against
    // someone with dev tools. If it ever needs to hold properly, the place
    // is partnerConfigFromVroomCode, which every session calls first.
    if (widgetLocked) {
        // Which screen the lock holds them on is the brand's choice. The
        // frozen first screen is for a partner who wants the embed to look
        // finished before they have paid; the holding screen is the default
        // and what every brand locked before the choice existed still gets.
        return widgetLockScreen === EWidgetLockScreen.FROZEN_FIRST_SCREEN ? (
            <WidgetLockedFirstScreen />
        ) : (
            <WidgetLocked />
        );
    }
    return <RealApp />;
}

export { Vroom };

function RealApp(): JSX.Element {
    const dispatch = useAppDispatch();
    const modal = useAppSelector(selectModal);
    const assetMake = useAppSelector(selectVehicleMake);
    const { cardGround } = useCopy();
    const partnerMakeWaterfallIdOverwrite = useAppSelector(
        (state) => state.displayReducer.partner.makeWaterfallIdOverwrite
    );

    // Whenever the asset make changes, check if there is a waterfall id overwrite for that make
    // And if there is, get the waterfall and set it in the store
    // Run the getQuickQuote function again to update the lenders
    useEffect(() => {
        if (assetMake && partnerMakeWaterfallIdOverwrite) {
            const lowercaseMake = assetMake
                .toLowerCase()
                .replace(/[^a-z0-9]/g, '');

            const waterfallMakeOverwriteArray = Object.entries(
                partnerMakeWaterfallIdOverwrite
            );
            for (const [make, waterfallId] of waterfallMakeOverwriteArray) {
                if (
                    make.toLowerCase().replace(/[^a-z0-9]/g, '') ===
                    lowercaseMake
                ) {
                    getWaterfallById(waterfallId)
                        .then((response) => {
                            if (response?.tiers) {
                                dispatch(setWaterfallLenders(response.tiers));
                                getQuickQuote();
                            }
                        })
                        .catch((err) => {
                            console.log(err);
                        });

                    // Leave the loop after the first match
                    break;
                }
            }
        }
    }, [assetMake]);

    return (
        <>
            <EventSubscriber
                eventName={EEvents.DOCUMENT_DELETE}
                onMessage={({ documentId }: { documentId: string }): void => {
                    dispatch(deleteSupportingDoc(documentId));
                }}
            />
            <EventSubscriber
                eventName={EEvents.DOCUMENT_UPLOAD}
                onMessage={(message: {
                    [code: string]: ISupportingDoc[];
                }): void => {
                    Object.entries(message).forEach(([key, value]) => {
                        dispatch(
                            setSupportingDocs({ code: key, document: value })
                        );
                    });
                }}
            />
            <div className='w-full h-full flex-center'>
                {/* Container for the actual application height and width */}
                <div
                    data-testid=''
                    className={`h-full w-full max-w-[394px] flex-center overflow-x-hidden relative ${
                        cardGround ? 'bg-pageGround' : 'bg-white'
                    } ${
                        modal && 'overflow-y-hidden bg-black bg-opacity-50'
                    }`}
                >
                    <ReviewNav />
                    <div
                        className={`mx-auto h-full w-full max-w-md  _flex flex-col relative`}
                    >
                        {modal && (
                            <div
                                className='fixed inset-0 bg-black opacity-30 z-50'
                                style={{ zIndex: 1 }}
                            />
                        )}
                        {/* Locked while a modal is open: scrolling the page
                            underneath moved content out from behind the modal
                            and left the reader unsure what they were on. */}
                        <div
                            className={`flex-grow ${
                                modal ? 'overflow-hidden' : 'overflow-y-auto'
                            }`}
                        >
                            <DisplayScreen />
                        </div>
                    </div>
                    {/* No scroll offset on the layer below: it is absolute
                        inside a container that does not scroll, so adding the
                        scroll position pushed the modal down by however far the
                        screen behind had been scrolled, and off centre. */}
                    {modal !== null && (
                        <div className='h-full w-full z-50 absolute top-0 flex-center'>
                            <div className='bg-white self-center w-[90%] rounded-xl max-h-[80%] mx-auto overflow-y-auto overflow-x-hidden p-[20px]'>
                                <ModalScreen />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
